import { Typography, Button, Empty, Card, Tag, Space } from 'antd';
import { PencilSimpleIcon } from '@phosphor-icons/react';
import { useAppSelector, useAppDispatch } from '../../../store/store';
import { useSearchPromptTemplatesInfiniteQuery } from '../../../store/stylist/stylistApi';
import {
  selectPromptTemplateFilters,
  setCurrentTemplate,
  setIsDrawerOpen,
} from '../../../store/stylist/slice';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { PromptTemplateListDTOType } from '../../../schemas/styling';
import UpsertPromptTemplateDrawer from '../UpsertPromptTemplateDrawer';
import useSyncWithUrl from '../useSyncWithUrl';
import * as Styles from './styles';

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  standard_workflow_prompt: 'Standard Workflow Prompt',
  occasion_workflow_prompt: 'Occasion Workflow Prompt',
  image_generation_prompt: 'Image Generation Prompt',
};

const List = () => {
  const activeFilters = useAppSelector(selectPromptTemplateFilters);
  const dispatch = useAppDispatch();
  const { openCreateDrawer, closeDrawer, isDrawerOpen } = useSyncWithUrl();

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchPromptTemplatesInfiniteQuery({
      filters: activeFilters,
    });

  const allTemplates = data?.pages.flatMap(page => page) || [];

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const openEditDrawer = (template: PromptTemplateListDTOType) => {
    // console.log('🚨 DEBUG: openEditDrawer called with:', template);
    dispatch(setCurrentTemplate(template));
    dispatch(setIsDrawerOpen(true));
    // console.log('🚨 DEBUG: Redux actions dispatched');
  };

  const PromptTemplateCard = ({ template }: { template: PromptTemplateListDTOType }) => (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderRadius: 8,
      }}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography.Text strong>{template.name}</Typography.Text>
          <Space>
            <Button
              type="text"
              size="small"
              icon={<PencilSimpleIcon />}
              onClick={() => openEditDrawer(template)}
              style={{ padding: '0 4px' }}
            />
            <Tag color={template.is_active ? 'green' : 'red'}>
              {template.is_active ? 'Active' : 'Inactive'}
            </Tag>
            <Tag color="blue">{TEMPLATE_TYPE_LABELS[template.template_type] ?? template.template_type}</Tag>
          </Space>
        </div>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">{template.description}</Typography.Text>
      </div>

      <div style={{ marginBottom: 12, maxHeight: 100, overflow: 'hidden' }}>
        <Typography.Text code style={{ fontSize: '12px' }}>
          {template.content?.substring(0, 200)}
          {template.content?.length > 200 ? '...' : ''}
        </Typography.Text>
      </div>
    </Card>
  );

  return (
    <Styles.Wrapper>
      <Styles.Header>
        <Typography.Paragraph
          strong
          style={{
            margin: 0,
          }}
        >
          Prompt Templates List
        </Typography.Paragraph>
        <Button type="primary" onClick={openCreateDrawer}>
          CREATE NEW TEMPLATE
        </Button>
      </Styles.Header>

      <Styles.Content>
        {isFetching && !isLoading && !isFetchingNextPage && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Typography.Text type="secondary">Updating results...</Typography.Text>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Typography.Text type="secondary">Loading prompt templates...</Typography.Text>
          </div>
        ) : allTemplates.length === 0 ? (
          <Empty description="No prompt templates found" style={{ padding: '50px' }} />
        ) : (
          <div>
            {allTemplates.map((template, index) => (
              <div
                key={template.id}
                ref={index === allTemplates.length - 1 ? lastItemRef : undefined}
              >
                <PromptTemplateCard template={template} />
              </div>
            ))}

            {isFetchingNextPage && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography.Text type="secondary">Loading more templates...</Typography.Text>
              </div>
            )}
          </div>
        )}
      </Styles.Content>

      <UpsertPromptTemplateDrawer open={isDrawerOpen} onClose={closeDrawer} />
    </Styles.Wrapper>
  );
};

export default List;
